import { Client, Project, User, Application, CriticalApp, TransformationStrategy, TransformationMaturity, FrameworkCategory, CustomerListItem, GoalsData, KeyGoal, PriorityGoal, TrendGoal, TechnologyResponse, AOPResponse, AOPItemCreateRequest, AOPItemResponse, FrameworksResponse, FrameworkItemRequest, DashboardApiResponse } from '../types';
import type { TechnologyItemRequest } from '../types';
import { fetchAuthSession } from 'aws-amplify/auth';
import { buildApiUrl, API_ENDPOINTS } from '../utils/api-config';
import { isDevBypassAuthEnabled, DEV_BYPASS_USER_ID } from '../utils/auth-utils';

// Helper function to get auth token
export const getAuthToken = async (): Promise<{idToken: string, userId: string}> => {
  if (isDevBypassAuthEnabled()) {
    return { idToken: 'dev-bypass-token', userId: DEV_BYPASS_USER_ID };
  }
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    
    if (!idToken) {
      throw new Error('No ID token found in session');
    }
    
    const decodedToken = decodeJWT(idToken);
    const userId = decodedToken.sub || 'unknown';
    
    return { idToken, userId };
  } catch (error) {
    console.error("Error getting auth token:", error);
    throw error;
  }
};

// Client API
export const clientApi = {
  getClient: async (id: string): Promise<Client> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // In a real implementation, this would fetch from an API
      const response = await fetch(buildApiUrl(`client/${id}`), {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`
        }
      });
      if (!response.ok) {
        throw new Error("Failed to fetch client");
      }
      return await response.json();
    } catch (error) {
      console.error(`Error fetching client with ID ${id}:`, error);
      // Return empty client when API call fails
      return {
        id,
        name: "",
        industry: "",
        profile: "",
        relationship: "",
      };
    }
  },

  updateClient: async (client: Client): Promise<Client> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // In a real implementation, this would update via an API
      const response = await fetch(buildApiUrl(`client/${client.id}`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify(client),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update client: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error updating client:`, error);
      // Return the provided client when API call fails
      return client;
    }
  },
};

// Customer API
export const customerApi = {
  getCustomerList: async (
    limit: number = 10,
    lastEvaluatedKey?: string
  ): Promise<{
    items: CustomerListItem[];
    count: number;
    total: number | null;
    lastEvaluatedKey?: string;
  }> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // Build the URL with pagination parameters
      let url = `${buildApiUrl(API_ENDPOINTS.CUSTOMERS)}?limit=${limit}`;
      if (lastEvaluatedKey) {
        url += `&lastEvaluatedKey=${encodeURIComponent(lastEvaluatedKey)}`;
      }

      // Include authentication headers
      const response = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`
        }
      });
      if (!response.ok) {
        throw new Error("Failed to fetch customers");
      }

      const result = await response.json();
      return {
        items: result.items || [],
        count: result.count || 0,
        total: result.total,
        lastEvaluatedKey: result.lastEvaluatedKey,
      };
    } catch (error) {
      console.error("Error fetching customer list:", error);
      // Return empty result when API call fails
      return {
        items: [],
        count: 0,
        total: null,
        lastEvaluatedKey: undefined,
      };
    }
  },

  getMyCustomerList: async (): Promise<CustomerListItem[]> => {
    try {
      const { idToken, userId } = await getAuthToken();

      const response = await fetch(buildApiUrl(API_ENDPOINTS.MY_CUSTOMERS), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch my customers");
      }
      const data = await response.json();
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error("Error fetching my customer list:", error);
      return [];
    }
  },

  getCustomerDetails: async (customerId: string): Promise<any> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      const response = await fetch(
        buildApiUrl(API_ENDPOINTS.CUSTOMER_BY_ID(customerId)),
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "user-id": userId,
            Authorization: `Bearer ${idToken}`
          }
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch customer details");
      }
      return await response.json();
    } catch (error) {
      console.error(
        `Error fetching customer details for ID ${customerId}:`,
        error
      );
    }
  },

  getDashboard: async (customerId: string): Promise<DashboardApiResponse> => {
    const { idToken, userId } = await getAuthToken();
    const response = await fetch(
      buildApiUrl(API_ENDPOINTS.CUSTOMER_DASHBOARD(customerId)),
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'user-id': userId,
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    if (!response.ok) {
      const message = await response.text();
      throw new Error(message || `Failed to fetch dashboard: ${response.status}`);
    }
    return response.json();
  },

  getCustomerFramework: async (customerId: string): Promise<FrameworksResponse> => {
    const { idToken, userId } = await getAuthToken();
    const response = await fetch(
      buildApiUrl(API_ENDPOINTS.CUSTOMER_FRAMEWORK(customerId)),
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'user-id': userId,
          Authorization: `Bearer ${idToken}`,
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch customer framework: ${response.status}`);
    }
    return await response.json();
  },

  createOrUpdateFrameworkItem: async (
    customerId: string,
    body: FrameworkItemRequest
  ): Promise<FrameworkItemRequest> => {
    const { idToken, userId } = await getAuthToken();
    const payload: FrameworkItemRequest = {
      framework_id: String(body.framework_id ?? '').trim(),
      focus: String(body.focus ?? '').trim(),
      category_id: String(body.category_id ?? '').trim(),
      category_name: String(body.category_name ?? '').trim(),
      division_id: String(body.division_id ?? '').trim(),
      division_name: String(body.division_name ?? '').trim(),
      metrics: Array.isArray(body.metrics) ? body.metrics.map(m => String(m)) : [],
    };
    const response = await fetch(
      buildApiUrl(API_ENDPOINTS.CUSTOMER_FRAMEWORK_CREATE(customerId)),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'user-id': String(userId ?? ''),
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(payload),
      }
    );
    if (!response.ok) {
      let message = `Failed to update framework item: ${response.status}`;
      try {
        const errBody = await response.json();
        if (errBody?.message) message = errBody.message;
        if (errBody?.details && typeof errBody.details === 'object') {
          const detailStr = JSON.stringify(errBody.details);
          if (detailStr !== '{}') message += ` ${detailStr}`;
        }
      } catch {
        /* use default message */
      }
      throw new Error(message);
    }
    return await response.json();
  },

  getCustomerTechnology: async (customerId: string): Promise<TechnologyResponse | null> => {
    try {
      const { idToken, userId } = await getAuthToken();
      const response = await fetch(
        buildApiUrl(API_ENDPOINTS.CUSTOMER_TECHNOLOGY(customerId)),
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'user-id': userId,
            Authorization: `Bearer ${idToken}`,
          },
        }
      );
      if (!response.ok) {
        return null;
      }
      const raw = await response.json();
      const technology_groups = (raw.technology_groups || []).map((g: { group?: string; technology_group_id?: string; technologies?: Array<{ technology_id?: string; name?: string; status?: string; notes?: string }> }) => ({
        group: g.group ?? '',
        technology_group_id: g.technology_group_id ?? '',
        technologies: (g.technologies || []).map((t: { technology_id?: string; name?: string; status?: string; notes?: string }) => ({
          technology_id: t.technology_id ?? '',
          name: t.name,
          status: t.status,
          notes: t.notes,
        })),
      }));
      const key_technology = technology_groups.map((g: { group: string; technologies: Array<{ name?: string; status?: string; notes?: string }> }) => ({
        name: g.group,
        divisions: (g.technologies || []).map((t: { name?: string; status?: string; notes?: string }) => ({
          tech: t.name ?? '',
          metric: t.status ?? '',
          description: t.notes ?? null,
        })),
      }));
      const strategic_vendors = (raw.strategic_vendors || []).map((v: { vendor?: string; status?: string; technology_id?: string }) => ({
        vendor: v.vendor ?? '',
        status: v.status ?? '',
        ...(v.technology_id != null && { technology_id: v.technology_id }),
      }));
      return { key_technology, strategic_vendors, technology_groups };
    } catch (error) {
      console.warn('Technology fetch failed', error);
      return null;
    }
  },

  createOrUpdateTechnologyItem: async (
    customerId: string,
    body: TechnologyItemRequest
  ): Promise<{ id: string; section: string; data: { name: string; status: string; notes: string } }> => {
    const { idToken, userId } = await getAuthToken();
    const response = await fetch(
      buildApiUrl(API_ENDPOINTS.CUSTOMER_TECHNOLOGY_CREATE(customerId)),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'user-id': userId,
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(body),
      }
    );
    if (!response.ok) {
      const errorBody = await response.text();
      let message = 'Failed to update technology item';
      try {
        const parsed = JSON.parse(errorBody);
        if (parsed?.message) message = parsed.message;
      } catch {
        if (errorBody) message = errorBody;
      }
      throw new Error(message);
    }
    return response.json();
  },

  createCustomer: async (customerData: {
    customer_details: {
      name: string;
      industry_type: string;
      created_by: string;
      ERPAccountNumber?: string;
    };
  }): Promise<{ customer_id: string; name?: string; industry_type?: string; created_by?: string; last_updated?: string }> => {
    const { idToken, userId } = await getAuthToken();

    const response = await fetch(
      buildApiUrl(API_ENDPOINTS.CUSTOMER_CREATE),
      {
        method: "POST",
        headers: {
          "user-id": userId,
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify(customerData),
      }
    );

    if (!response.ok) {
      const errorBody = await response.text();
      let message = "Failed to create customer";
      try {
        const parsed = JSON.parse(errorBody);
        if (parsed?.message) message = parsed.message;
      } catch {
        if (errorBody) message = errorBody;
      }
      throw new Error(message);
    }

    return response.json();
  },

  updateCustomerDetails: async (customerId: string, updateData: any): Promise<any> => {
    try {
      const { idToken, userId } = await getAuthToken();

      const response = await fetch(
        buildApiUrl(API_ENDPOINTS.CUSTOMER_BY_ID(customerId)),
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'user-id': userId,
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify(updateData),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let message = `Failed to update customer details: ${response.status}`;
        try {
          const body = text ? JSON.parse(text) : {};
          if (body?.message) message = body.message;
        } catch {
          if (text) message = text;
        }
        console.error(`Update customer details error: ${response.status}`, message);
        throw new Error(message);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error updating customer details for ID ${customerId}:`, error);
      throw error;
    }
  },

  // Goals APIs (getAllGoals, createGoal, updateGoal, deleteGoal)
  getProjectsByGoalId: async (
    customerId: string,
    goalId: string
  ): Promise<{ attached: boolean; projects: Array<{ projectId: string; projectName: string }> }> => {
    const { idToken, userId } = await getAuthToken();
    const response = await fetch(buildApiUrl(API_ENDPOINTS.CUSTOMER_GOAL_PROJECTS(customerId, goalId)), {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'user-id': userId,
        Authorization: `Bearer ${idToken}`,
      },
    });
    if (!response.ok) throw new Error('Failed to fetch projects by goal');
    return response.json();
  },

  getAllGoals: async (customerId: string, section?: 'KEY_GOALS' | 'PRIORITY_GOALS' | 'TREND_GOALS'): Promise<GoalsData> => {
    const { idToken, userId } = await getAuthToken();
    let url = buildApiUrl(API_ENDPOINTS.CUSTOMER_GOALS_LIST(customerId));
    if (section) url += `?section=${section}`;
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'user-id': userId,
        Authorization: `Bearer ${idToken}`,
      },
    });
    if (!response.ok) throw new Error('Failed to fetch goals');
    const data = await response.json();
    return {
      key_goals: data.key_goals ?? [],
      priority_goals: data.priority_goals ?? [],
      trend_goals: data.trend_goals ?? [],
    };
  },

  createGoal: async (
    customerId: string,
    section: 'KEY_GOALS' | 'PRIORITY_GOALS' | 'TREND_GOALS',
    data: Partial<KeyGoal> | Partial<PriorityGoal> | Partial<TrendGoal>
  ): Promise<{ customer_id: string; goal_id: string; section: string; data: unknown }> => {
    const { idToken, userId } = await getAuthToken();
    const payload =
      section === 'KEY_GOALS' && data && 'definition' in data
        ? { goal: (data as Partial<KeyGoal>).goal, description: (data as Partial<KeyGoal>).definition }
        : data;
    const response = await fetch(buildApiUrl(API_ENDPOINTS.CUSTOMER_GOALS_CREATE(customerId)), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'user-id': userId,
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ section, data: payload }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to create goal');
    }
    return response.json();
  },

  updateGoal: async (
    customerId: string,
    id: string,
    section: 'KEY_GOALS' | 'PRIORITY_GOALS' | 'TREND_GOALS',
    data: Partial<KeyGoal> | Partial<PriorityGoal> | Partial<TrendGoal>
  ): Promise<{ customer_id: string; goal_id: string; section: string; data: unknown }> => {
    const { idToken, userId } = await getAuthToken();
    const payload =
      section === 'KEY_GOALS' && data && 'definition' in data
        ? { goal: (data as Partial<KeyGoal>).goal, description: (data as Partial<KeyGoal>).definition }
        : data;
    const response = await fetch(buildApiUrl(API_ENDPOINTS.CUSTOMER_GOAL(customerId)), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'user-id': userId,
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ id, section, data: payload }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to update goal');
    }
    return response.json();
  },

  deleteGoal: async (
    customerId: string,
    section: 'KEY_GOALS' | 'PRIORITY_GOALS' | 'TREND_GOALS',
    id: string,
    projectIds: string[] = []
  ): Promise<{ customer_id: string; deleted_id: string }> => {
    const { idToken, userId } = await getAuthToken();
    const response = await fetch(buildApiUrl(API_ENDPOINTS.CUSTOMER_GOAL(customerId)), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'user-id': userId,
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ section, id, projectIds }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to delete goal');
    }
    return response.json();
  },
};

// Project API
export const projectApi = {
  getProjects: async (customer_id:string): Promise<Project[]> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // In a real implementation, this would fetch from an API
      const response = await fetch(buildApiUrl(API_ENDPOINTS.CUSTOMER_PROJECTS(customer_id)), {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`,
          "Cache-Control": "no-cache, no-store, must-revalidate",
          "Pragma": "no-cache",
          "Expires": "0"
        }
      });
      if (!response.ok) {
        throw new Error("Failed to fetch projects");
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching projects:", error);
      // Return empty array when API call fails
      return [];
    }
  },

 
  updateProject: async (project: Project, customerId: string): Promise<Project> => {
    try {
      // Get auth token
      // const { idToken, userId } = await getAuthToken();
      
      // Note: There might not be a direct update endpoint for projects
      // For now, we'll return the project as-is and log a warning
      console.warn('Project update endpoint not implemented. Returning project as-is.');
      
      // TODO: Implement project update when backend endpoint is available
      // const response = await fetch(`https://api.dev.clientwiki.app.presidio.com/customer/${customerId}/projects/${project.id}`, {
      //   method: 'PUT',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     Accept: "application/json",
      //     "user-id": userId,
      //     Authorization: `Bearer ${idToken}`
      //   },
      //   body: JSON.stringify(project),
      // });
      
      // if (!response.ok) {
      //   throw new Error(`Failed to update project: ${response.status}`);
      // }
      
      // return await response.json();
      
      // Return the provided project when API call fails or not implemented
      return project;
    } catch (error) {
      console.error(`Error updating project:`, error);
      // Return the provided project when API call fails
      return project;
    }
  },

  deleteProject: async (projectId: string, customerId: string): Promise<boolean> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // Use customer-specific delete endpoint
      const response = await fetch(`${buildApiUrl(API_ENDPOINTS.CUSTOMER_DELETE(customerId))}?project_id=${projectId}`, {
        method: 'DELETE',
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to delete project: ${response.status} - ${errorText}`);
        throw new Error(`Failed to delete project: ${response.status}`);
      }
      
      return true;
    } catch (error) {
      console.error(`Error deleting project:`, error);
      // Return true to indicate success when API call fails (optimistic UI)
      return true;
    }
  },
};

// Applications API
export const applicationsApi = {
  getApplications: async (customerId: string): Promise<CriticalApp[]> => {
    try {
      const { idToken, userId } = await getAuthToken();
      const response = await fetch(buildApiUrl(API_ENDPOINTS.CUSTOMER_APPLICATIONS(customerId)), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'user-id': userId,
          Authorization: `Bearer ${idToken}`,
        },
      });
      if (!response.ok) {
        if (response.status === 404) {
          return [];
        }
        const errorText = await response.text();
        throw new Error(errorText || `Failed to fetch applications: ${response.status}`);
      }
      const data = await response.json();
      const rawList = Array.isArray(data) ? data : (data.critical_apps_list ?? data.applications ?? []);
      const list = Array.isArray(rawList) ? rawList : [];
      return list.map((item: { application_id?: string; app_id?: string; [key: string]: unknown }) => ({
        ...item,
        app_id: item.application_id ?? item.app_id,
      })) as CriticalApp[];
    } catch (error) {
      console.error('Error fetching applications:', error);
      return [];
    }
  },

  addApplication: async (
    application: Omit<Application, "id">,
    customerId: string
  ): Promise<Application> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // Use customer-specific application endpoint
      const response = await fetch(buildApiUrl(API_ENDPOINTS.CUSTOMER_APPLICATION_CREATE(customerId)), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify(application),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to add application: ${response.status} - ${errorText}`);
        throw new Error(`Failed to add application: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error adding application:`, error);
      // Return minimal object with application data when API call fails
      return {
        id: `temp-${Date.now()}`,
        ...application,
      };
    }
  },

  updateApplication: async (
    customerId: string,
    applicationId: string,
    body: { name: string; usage: string; location: string; tier: number; customer_facing_notes: string | null; internal_notes: string | null }
  ): Promise<CriticalApp> => {
    const { idToken, userId } = await getAuthToken();
    const response = await fetch(buildApiUrl(API_ENDPOINTS.CUSTOMER_APPLICATION_BY_ID(customerId, applicationId)), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'user-id': userId,
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Failed to update application: ${response.status}`);
    }
    return response.json();
  },

  deleteApplication: async (customerId: string, applicationId: string): Promise<void> => {
    const { idToken, userId } = await getAuthToken();
    const response = await fetch(buildApiUrl(API_ENDPOINTS.CUSTOMER_APPLICATION_BY_ID(customerId, applicationId)), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'user-id': userId,
        Authorization: `Bearer ${idToken}`,
      },
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Failed to delete application: ${response.status}`);
    }
  },

  updateApplicationNotes: async (
    id: string,
    type: "customer" | "internal",
    notes: string
  ): Promise<Application> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // In a real implementation, this would update the notes via an API
      const response = await fetch(buildApiUrl(API_ENDPOINTS.APPLICATION_NOTES(id)), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          type,
          notes
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update application notes: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error updating application notes:`, error);
      // Return minimal object with updated notes when API call fails
      return {
        id,
        entry: "",
        name: "",
        usage: "",
        location: "",
        tier: 0,
        customer_facing_notes: type === "customer" ? notes : null,
        internal_notes: type === "internal" ? notes : null,
      };
    }
  },
};

// Art of Possible API (new backend endpoints)
export const aopApi = {
  getCustomerAop: async (customerId: string): Promise<AOPResponse> => {
    const { idToken, userId } = await getAuthToken();
    const response = await fetch(buildApiUrl(API_ENDPOINTS.CUSTOMER_AOP(customerId)), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'user-id': userId,
        Authorization: `Bearer ${idToken}`,
      },
    });
    if (!response.ok) {
      if (response.status === 403 || response.status === 404) {
        return { customer_id: customerId, aop_items: [] };
      }
      const errorText = await response.text();
      throw new Error(errorText || `Failed to fetch AOP: ${response.status}`);
    }
    return response.json();
  },

  createOrUpdateCustomerAop: async (
    customerId: string,
    items: AOPItemCreateRequest[]
  ): Promise<{ items: AOPItemResponse[] }> => {
    const { idToken, userId } = await getAuthToken();
    const response = await fetch(buildApiUrl(API_ENDPOINTS.CUSTOMER_AOP_CREATE(customerId)), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'user-id': userId,
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(items),
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(errorText || `Failed to update AOP: ${response.status}`);
    }
    return response.json();
  },
};

// Art of Possible API (legacy – kept for reference; use aopApi for new integration)
export const artOfPossibleApi = {
  getTransformationStrategies: async (): Promise<TransformationStrategy[]> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // In a real implementation, this would fetch from an API
      const response = await fetch(buildApiUrl(API_ENDPOINTS.TRANSFORMATION_STRATEGIES), {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`
        }
      });
      if (!response.ok) {
        throw new Error("Failed to fetch transformation strategies");
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching transformation strategies:", error);
      // Return empty array when API call fails
      return [];
    }
  },

  updateTransformationStrategy: async (
    strategy: TransformationStrategy
  ): Promise<TransformationStrategy> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // In a real implementation, this would update via an API
      const response = await fetch(buildApiUrl(API_ENDPOINTS.TRANSFORMATION_STRATEGY_BY_ID(strategy.id)), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify(strategy),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update transformation strategy: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error updating transformation strategy:`, error);
      // Return the provided strategy when API call fails
      return strategy;
    }
  },

  bulkUpdateTransformationStrategies: async (
    strategies: TransformationStrategy[]
  ): Promise<TransformationStrategy[]> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // In a real implementation, this would update via an API
      const response = await fetch(buildApiUrl(API_ENDPOINTS.TRANSFORMATION_STRATEGIES_BULK), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({ strategies }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to bulk update transformation strategies: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error bulk updating transformation strategies:`, error);
      // Return the provided strategies when API call fails
      return strategies;
    }
  },
};

// Transformation Maturity API
export const transformationApi = {
  getTransformationMaturity: async (): Promise<TransformationMaturity[]> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // In a real implementation, this would fetch from an API
      const response = await fetch(buildApiUrl(API_ENDPOINTS.TRANSFORMATION_MATURITY), {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`
        }
      });
      if (!response.ok) {
        throw new Error("Failed to fetch transformation maturity data");
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching transformation maturity data:", error);
      // Return empty array when API call fails
      return [];
    }
  },

  updateTransformationNotes: async (
    id: string,
    notes: string
  ): Promise<TransformationMaturity> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // In a real implementation, this would update via an API
      const response = await fetch(buildApiUrl(API_ENDPOINTS.TRANSFORMATION_MATURITY_NOTES(id)), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({ notes }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update transformation notes: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error updating transformation notes:`, error);
      // Return minimal object with updated notes when API call fails
      return {
        step_id:"",
        transformationArea: "",
        priority: "",
        growth: "",
        notes,
      };
    }
  },

  updateTransformationPriority: async (
    id: string,
    priority: string
  ): Promise<TransformationMaturity> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // In a real implementation, this would update via an API
      const response = await fetch(buildApiUrl(API_ENDPOINTS.TRANSFORMATION_MATURITY_PRIORITY(id)), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({ priority }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update transformation priority: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error updating transformation priority:`, error);
      // Return minimal object with updated priority when API call fails
      return {
        step_id:"",
        transformationArea: "",
        priority,
        growth: "",
        notes: "",
      };
    }
  },

  updateTransformationGrowth: async (
    id: string,
    growth: string
  ): Promise<TransformationMaturity> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // In a real implementation, this would update via an API
      const response = await fetch(buildApiUrl(API_ENDPOINTS.TRANSFORMATION_MATURITY_GROWTH(id)), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({ growth }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update transformation growth: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error updating transformation growth:`, error);
      // Return minimal object with updated growth when API call fails
      return {
        step_id:"",
        transformationArea: "",
        priority: "",
        growth,
        notes: "",
      };
    }
  },
};

// Frameworks API
export const frameworksApi = {
  getFrameworkCategories: async (): Promise<FrameworkCategory[]> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // In a real implementation, this would fetch from an API
      const response = await fetch(buildApiUrl(API_ENDPOINTS.FRAMEWORK_CATEGORIES), {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`
        }
      });
      if (!response.ok) {
        throw new Error("Failed to fetch framework categories");
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching framework categories:", error);
      // Return empty array when API call fails
      return [];
    }
  },

  getFrameworkTools: async (): Promise<Record<string, string[]>> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // In a real implementation, this would fetch from an API
      const response = await fetch(buildApiUrl(API_ENDPOINTS.FRAMEWORK_TOOLS), {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`
        }
      });
      if (!response.ok) {
        throw new Error("Failed to fetch framework tools");
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching framework tools:", error);
      // Return empty object when API call fails
      return {};
    }
  },

  updateFrameworkTools: async (
    categoryId: string,
    tools: string[]
  ): Promise<string[]> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // In a real implementation, this would update via an API
      const response = await fetch(buildApiUrl(API_ENDPOINTS.FRAMEWORK_TOOLS_BY_CATEGORY(categoryId)), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({ tools }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update framework tools: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error updating framework tools:`, error);
      // Return the provided tools when API call fails
      return tools;
    }
  },

  addFrameworkTool: async (
    categoryId: string,
    toolName: string
  ): Promise<{ id: string; name: string }> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // In a real implementation, this would add via an API
      const response = await fetch(buildApiUrl(API_ENDPOINTS.FRAMEWORK_TOOLS_ADD(categoryId)), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({ name: toolName }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to add framework tool: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error adding framework tool:`, error);
      // Return minimal object with tool name when API call fails
      return {
        id: `temp-${Date.now()}`,
        name: toolName,
      };
    }
  },

  deleteFrameworkTool: async (
    categoryId: string,
    toolName: string
  ): Promise<boolean> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // In a real implementation, this would delete via an API
      const response = await fetch(buildApiUrl(API_ENDPOINTS.FRAMEWORK_TOOLS_DELETE(categoryId)), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({ name: toolName }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete framework tool: ${response.status}`);
      }
      
      return true;
    } catch (error) {
      console.error(`Error deleting framework tool:`, error);
      // Return true to indicate success when API call fails (optimistic UI)
      return true;
    }
  },

  reorderFrameworkTools: async (
    categoryId: string,
    tools: string[]
  ): Promise<string[]> => {
    try {
      // Get auth token
      const { idToken, userId } = await getAuthToken();
      
      // In a real implementation, this would reorder via an API
      const response = await fetch(buildApiUrl(API_ENDPOINTS.FRAMEWORK_TOOLS_REORDER(categoryId)), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: "application/json",
          "user-id": userId,
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({ tools }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to reorder framework tools: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error reordering framework tools:`, error);
      // Return the provided tools when API call fails
      return tools;
    }
  },
};

// Function to decode JWT token
const decodeJWT = (token: string): any => {
  try {
    // JWT tokens are base64 encoded with 3 parts: header.payload.signature
    const base64Url = token.split('.')[1]; // Get the payload part
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT token:', error);
    return {};
  }
};

// User API
export const userApi = {
  getCurrentUser: async (): Promise<User> => {
    try {
      // Get the auth session which contains the tokens
      const session = await fetchAuthSession();
      
      // Get the ID token
      const idToken = session.tokens?.idToken?.toString();
      
      if (!idToken) {
        throw new Error('No ID token found in session');
      }
      
      // Decode the ID token to get user information
      const decodedToken = decodeJWT(idToken);
      
      // Extract relevant user information from the decoded token
      return {
        id: decodedToken.sub || decodedToken.email || 'unknown',
          name: decodedToken.given_name + " "+ decodedToken.family_name || decodedToken.email || 'Unknown User',
          role: decodedToken['custom:job_title'] || 'User',
        avatar: decodedToken.picture || '',
        email: decodedToken.email || ''
      };
    } catch (error) {
      console.error("Error fetching current user:", error);
      // Return default user when authentication fails
      return {
        id: "guest",
        name: "Guest User",
        role: "Guest",
        avatar: "",
        email:""
      };
    }
  },
};
